"use client";

import { useEffect, useRef, useState } from "react";

/**
 * Counts up from 0 to `value` when the element scrolls into view.
 * The final `value` is rendered on the server so crawlers (and JS-disabled
 * visitors) always see the real number — the animation is a client-only
 * enhancement that starts once the element is observed.
 */
export default function Counter({
  value,
  duration = 1800,
  className,
}: {
  value: number;
  duration?: number;
  className?: string;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  // SSR + first client render show the real value (no hydration mismatch, SEO-safe).
  const [display, setDisplay] = useState(value);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Respect users who prefer reduced motion — keep the final value, no animation.
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    // Reset to 0 on the client so the number is primed to count up on scroll.
    setDisplay(0);

    let raf = 0;
    let startTs = 0;
    let started = false;

    const step = (now: number) => {
      if (!startTs) startTs = now;
      const progress = Math.min((now - startTs) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // easeOutCubic
      setDisplay(Math.round(eased * value));
      if (progress < 1) raf = requestAnimationFrame(step);
    };

    const animate = () => {
      if (started) return;
      started = true;
      raf = requestAnimationFrame(step);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          animate();
          observer.disconnect();
        }
      },
      { threshold: 0.25 }
    );
    observer.observe(el);

    return () => {
      observer.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [value, duration]);

  return (
    <span ref={ref} className={className}>
      {display.toLocaleString()}
    </span>
  );
}
